/**
 * Tool slice — mesh generation parameters + active tool.
 *
 * Aggregate setters (setMeshSettings, setBoundaryLayerSettings, setStlSettings)
 * are for agent use and auto-sync string variants.
 */
import type { StateCreator } from 'zustand';
import type {
  BodyRegion,
  DomainParams,
  EngineOverrides,
  QualityTarget,
} from '../../api/generated';
import type { FaceKey } from '../../utils/externalFlowFaces';
import type { SurfaceType } from '../../utils/faceCatalog';
import type { RefinementZoneDraft } from '../../utils/refinementZones';
import { PER_RUN_OVERRIDE_DEFAULTS } from '../../utils/engineOverrides';
import { domainModeForPhysics } from '../../utils/simulationType';
import {
  DEFAULTS,
  DOMAIN_DEFAULTS,
  type DirectionKey,
  ALL_DIRECTION_KEYS,
  resolveDirectionFactors,
  computePresetSizes,
  type GeometryCharacteristics,
} from '../../constants';
import type { StoreState } from '../useStore';
import type { AutoRetryValue } from '../../components/AutoRetryFieldGroup';
import {
  DEFAULT_MESH_AUTO_RETRY_MAX_ATTEMPTS,
  sameAutoRetryConfig,
} from '../../utils/meshRetry';

export type WallGroup = {
  face_index: number;
  part: string | null;
  label: string;
  centroid?: [number, number, number];
  normal?: [number, number, number];
  area?: number;
  triangle_count: number;
};

export type DetectedOpening = {
  faceId: string;
  name: string;
  label: string;
  centroid: [number, number, number];
  normal: [number, number, number];
  area: number; // mm²
  face_index: number | null; // exact-match key for backend; null when from legacy mock data
  part: string | null; // STEP product name from occt-import-js; null until viewer pick is wired
  // The surface the face lies on and its radius (ENG-2941), for the panel's
  // read-out only — never sent with the mesh request. Absent when unread.
  surface_type?: SurfaceType | null;
  radius_mm?: number | null;
};

const BL = DEFAULTS.BOUNDARY_LAYER;

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface ToolSlice {
  // Mesh params
  qualityPreset: string;
  maxElementSize: number;
  minElementSize: number;
  elementOrder: string;
  volumeCoupling: 'shared_nodes' | 'independent';
  ansysMode: string;
  meshStrategy: string | null;
  curvatureAdaptive: boolean;
  meshType: string;
  simulationType: 'cfd' | 'fea' | 'cht';
  elementType: 'tet' | 'hex' | 'poly';
  // Seconds; null = use the strategy's built-in default timeout.
  strategyTimeoutS: number | null;

  // STL preprocessing
  stlBeforeMeshEnabled: boolean;
  stlSurfaceFactor: number;
  stlCurvaturePts: number;
  stlMinCircleNodes: number;

  // String mirrors (for form inputs)
  maxSizeStr: string;
  minSizeStr: string;
  stlSurfaceFactorStr: string;
  stlCurvaturePtsStr: string;
  stlMinCircleNodesStr: string;

  // Domain / enclosure params
  enclosureEnabled: boolean;
  domainParams: DomainParams;
  faceTagAssignments: DetectedOpening[];
  boundaryAssignmentOpen: boolean;
  bodyClassificationOpen: boolean;
  // FEA body assignment (ENG-2214). A separate flag from
  // `bodyClassificationOpen` because the two panels edit different records —
  // CHT roles live in the mesh draft, structural roles on the geometry — and
  // one flag would open whichever panel the simulation type happened to be on.
  structuralBodiesOpen: boolean;
  blOpen: boolean;
  // Geometry (currentProject) whose Boundary Layer submenu has already had its
  // one-time default applied this session — first open auto-enables BL (and
  // defaults Prioritize BL coverage on). Session-only (not persisted): a later
  // open of the same geometry sees the key match and leaves the user's choice
  // alone. Null until the first BL submenu open.
  blAutoInitializedFor: string | null;
  // Whether the Advanced collapsible in MeshSettingsPanel (Engine
  // Overrides + Element Order etc.) is expanded. Lives in the store
  // so the validation-chip click-to-focus mechanic can programmatically
  // open it when the user jumps to a field inside.
  advancedOverridesOpen: boolean;
  domainAbsoluteMode: boolean;
  externalFaceOverrides: Partial<Record<FaceKey, string>>;
  directionFactorStrs: Record<DirectionKey, string>;

  engineOverrides: EngineOverrides;

  // Boundary layers
  boundaryLayersEnabled: boolean;
  blLayers: number;
  blRatio: number;
  blThicknessUser: number;
  blLayersStr: string;
  blRatioStr: string;
  blThicknessUserStr: string;
  // Optional schema fields — y+ auto-compute (no UI yet)
  blYPlus: number | null;
  blRe: number | null;

  blGlobalEnabled: boolean;
  blPatchNames?: string[] | null;
  // True when the fluid-domain (cavity) STL is currently rendered in the
  // viewer. Owned by `MeshViewer`'s render effect. False covers all of: not
  // requested, still loading, fetch error, parse error. Readers: Body
  // Classification's paint guard and the BL cavity-vs-body banner. Submit's
  // `face_tag_source` does not read it (`buildMeshParams` uses
  // `faceSurfaceSource`).
  cavityStlRendered: boolean;
  // User-chosen mesh/pick source ("Mesh from") for CFD internal flow and CHT
  // with an extracted fluid domain: 'fluid_domain' (cavity STL — default) or
  // 'body' (the body Fine STL, e.g. an uploaded fluid.stp that already IS the
  // fluid). Drives the viewer display (showFluidDomain) in LR/BA/BL/BC and,
  // through `faceSurfaceSource`, face_tag_source on submit. Only meaningful
  // when has_fluid_domain; ignored otherwise.
  internalFlowMeshSource: 'fluid_domain' | 'body';
  wallGroups: WallGroup[];

  // CHT multi-body classification
  bodyRegions: BodyRegion[];
  // The last classification the user actually held for a given descriptor set,
  // so a clear-and-return does not silently re-seed over it (ENG-1607). Keyed by
  // `descriptorSignature` (geometry id + body_index list). Several owners clear
  // `bodyRegions` for their own reasons — project switch, the embedded_fluid
  // draft reset, the style flip back to extraction (plus the currently-unmounted
  // SimOps sync's inert default) — and the CHT seed then ran again and put
  // `fluid` back on descriptor index 0, which for any part whose fluid is the
  // extracted cavity is deterministically the WRONG body (the cavity is
  // appended last, so it can never be index 0).
  //
  // Deliberately NOT in `pickAuthoringDraft`, so it is session-scoped. NOT
  // because a clear would erase it — an earlier version of this comment said so
  // and was wrong; `setBodyRegions([])` writes only `bodyRegions`, so a memo in
  // the draft would be saved still holding the user's roles. It is out because
  // persisting it would leave the draft carrying two disagreeing facts across a
  // reload. Full reasoning and the two known limits are on
  // `restoreOrSeedBodyRegions` in `components/mesh/bodyRegionSeed.ts`.
  bodyRegionsMemo: { signature: string; regions: BodyRegion[] } | null;

  // Local refinement zones (MeshParams.refinement_zones drafts, hex only).
  // World-frame mm boxes/spheres authored via the Local Refinement sub-panel
  // and the viewer's Ctrl+click pick — both write here (the viewer sits
  // outside the authoring provider, so this is the single source of truth; the
  // provider re-syncs via the BoundaryLayerPanel dual-source pattern).
  refinementZones: RefinementZoneDraft[];
  // Whether the Local Refinement sub-panel is open. Gates the viewer's zone
  // overlay and the Ctrl+click fit-to-face pick, like blOpen does for BL.
  refinementZonesOpen: boolean;

  // Post-mesh quality gate (ENG-900). null = no target; metric is coupled to
  // elementType (sicn for tet, non_orthogonality for hex).
  qualityTarget: QualityTarget | null;
  // Mesh AI auto-retry opt-in (ENG-1185). null = off (default). Rides inside
  // MeshParams as `auto_retry: {enabled, max_attempts}` at submit
  // (buildMeshParams) — never a request-body sibling. max_attempts 1..3
  // (contracts §1.1).
  meshAutoRetry: AutoRetryValue | null;
  // Body Classification view mode — which STL the viewer renders while
  // the BC panel is open. `cavity` shows the fluid passage; `body` shows the
  // wall geometry so solid volumes can be inspected. Only meaningful in CHT
  // mode. Always starts as `cavity`. When the coolant can't be shown (not
  // carved, or "Mesh from" on Body) the panel drops it to `body` (ENG-1919,
  // ENG-3115), and it stays `body` for the session, across projects too,
  // until the user picks Cavity. Not saved in drafts. Repair only: the wrap
  // panel's Cavity / Body is `previewRepresentation` (ENG-3119).
  bodyClassificationView: 'body' | 'cavity';

  // Individual setters
  setQualityPreset: (v: string) => void;
  setMaxElementSize: (v: number) => void;
  setMinElementSize: (v: number) => void;
  setElementOrder: (v: string) => void;
  setAnsysMode: (v: string) => void;
  setMeshStrategy: (v: string | null) => void;
  setStrategyTimeoutS: (v: number | null) => void;
  setCurvatureAdaptive: (v: boolean) => void;
  setMeshType: (v: string) => void;
  setSimulationType: (v: 'cfd' | 'fea' | 'cht') => void;
  setElementType: (v: 'tet' | 'hex' | 'poly') => void;
  setStlBeforeMeshEnabled: (v: boolean) => void;
  setStlSurfaceFactor: (v: number) => void;
  setStlCurvaturePts: (v: number) => void;
  setStlMinCircleNodes: (v: number) => void;
  setMaxSizeStr: (v: string) => void;
  setMinSizeStr: (v: string) => void;
  setStlSurfaceFactorStr: (v: string) => void;
  setStlCurvaturePtsStr: (v: string) => void;
  setStlMinCircleNodesStr: (v: string) => void;
  setEnclosureEnabled: (v: boolean) => void;
  setDomainParams: (patch: Partial<DomainParams>) => void;
  clearOpeningEntries: () => void;
  setFaceTagAssignments: (v: DetectedOpening[]) => void;
  setBoundaryAssignmentOpen: (v: boolean) => void;
  setBodyClassificationOpen: (v: boolean) => void;
  setStructuralBodiesOpen: (v: boolean) => void;
  setBlOpen: (v: boolean) => void;
  setBlAutoInitializedFor: (v: string | null) => void;
  setAdvancedOverridesOpen: (v: boolean) => void;
  setDomainAbsoluteMode: (v: boolean) => void;
  setExternalFaceOverride: (faceKey: FaceKey, label: string | null) => void;
  resetExternalFaceOverrides: () => void;
  setDirectionFactorStr: (key: DirectionKey, v: string) => void;
  setEngineOverrides: (v: Partial<EngineOverrides>) => void;
  setBoundaryLayersEnabled: (v: boolean) => void;
  setBlLayers: (v: number) => void;
  setBlRatio: (v: number) => void;
  setBlThicknessUser: (v: number) => void;
  setBlLayersStr: (v: string) => void;
  setBlRatioStr: (v: string) => void;
  setBlThicknessUserStr: (v: string) => void;
  setBlYPlus: (v: number | null) => void;
  setBlRe: (v: number | null) => void;
  setBlGlobalEnabled: (v: boolean) => void;
  setBlPatchNames: (v: string[] | null | undefined) => void;
  setCavityStlRendered: (v: boolean) => void;
  setInternalFlowMeshSource: (v: 'fluid_domain' | 'body') => void;
  setWallGroups: (groups: WallGroup[]) => void;
  addWallGroup: (group: WallGroup) => void;
  removeWallGroup: (face_index: number) => void;
  setWallGroupLabel: (face_index: number, label: string) => void;
  setBodyRegions: (regions: BodyRegion[]) => void;
  setBodyRegionsMemo: (
    memo: { signature: string; regions: BodyRegion[] } | null,
  ) => void;
  setBodyClassificationView: (v: 'body' | 'cavity') => void;
  setQualityTarget: (v: QualityTarget | null) => void;
  setMeshAutoRetry: (cfg: AutoRetryValue | null) => void;
  setRefinementZones: (zones: RefinementZoneDraft[]) => void;
  updateRefinementZone: (
    id: string,
    patch: Partial<RefinementZoneDraft>,
  ) => void;
  setRefinementZonesOpen: (v: boolean) => void;

  // Aggregate setters (for agent use)
  setMeshSettings: (
    patch: Partial<
      Pick<
        ToolSlice,
        | 'qualityPreset'
        | 'maxElementSize'
        | 'minElementSize'
        | 'elementOrder'
        | 'ansysMode'
        | 'meshStrategy'
        | 'curvatureAdaptive'
        | 'meshType'
        | 'simulationType'
        | 'elementType'
      >
    >,
  ) => void;
  setBoundaryLayerSettings: (
    patch: Partial<
      Pick<
        ToolSlice,
        | 'boundaryLayersEnabled'
        | 'blLayers'
        | 'blRatio'
        | 'blThicknessUser'
        | 'blYPlus'
        | 'blRe'
      >
    >,
  ) => void;
  setStlSettings: (
    patch: Partial<
      Pick<
        ToolSlice,
        | 'stlBeforeMeshEnabled'
        | 'stlSurfaceFactor'
        | 'stlCurvaturePts'
        | 'stlMinCircleNodes'
      >
    >,
  ) => void;

  resetMeshParamsState: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function syncStrings(patch: Record<string, unknown>): Record<string, unknown> {
  const synced: Record<string, unknown> = { ...patch };
  if ('maxElementSize' in patch)
    synced.maxSizeStr = String(patch.maxElementSize);
  if ('minElementSize' in patch)
    synced.minSizeStr = String(patch.minElementSize);
  if ('stlSurfaceFactor' in patch)
    synced.stlSurfaceFactorStr = String(patch.stlSurfaceFactor);
  if ('stlCurvaturePts' in patch)
    synced.stlCurvaturePtsStr = String(patch.stlCurvaturePts);
  if ('stlMinCircleNodes' in patch)
    synced.stlMinCircleNodesStr = String(patch.stlMinCircleNodes);
  if ('blLayers' in patch) synced.blLayersStr = String(patch.blLayers);
  if ('blRatio' in patch) synced.blRatioStr = String(patch.blRatio);
  if ('blThicknessUser' in patch)
    synced.blThicknessUserStr = String(patch.blThicknessUser);
  return synced;
}

/**
 * Order 2 (C3D10) is FEA-and-tet only, and the store opens on it (ENG-2672).
 * A patch that moves off FEA or off tet without naming an order would
 * otherwise carry '2' into a CFD/CHT or hex mesh — the agent's
 * `setMeshSettings` and the per-project restore both write this way.
 */
function withLinearOrderOffFeaTet<
  P extends Partial<
    Pick<ToolSlice, 'elementOrder' | 'simulationType' | 'elementType'>
  >,
>(patch: P): P {
  if ('elementOrder' in patch) return patch;
  const leavesFea =
    patch.simulationType != null && patch.simulationType !== 'fea';
  const leavesTet = patch.elementType != null && patch.elementType !== 'tet';
  return leavesFea || leavesTet ? { ...patch, elementOrder: '1' } : patch;
}

/**
 * The buttons write the physics and its mode together; the assistant and the
 * project reload write the physics alone, so the mode follows here (ENG-3150).
 * The wind-tunnel flag is left alone: the panel's copy isn't fed from the
 * store, so moving it here would leave the two out of step (ENG-3151).
 */
function modeFollowingPhysics(
  simulationType: ToolSlice['simulationType'] | undefined,
  domainParams: DomainParams,
): Partial<Pick<ToolSlice, 'domainParams'>> {
  if (simulationType == null) return {};
  const mode = domainModeForPhysics(simulationType, domainParams.mode);
  if (mode === domainParams.mode) return {};
  return { domainParams: { ...domainParams, mode } };
}

function applyPresetSizesForMeshPatch(
  patch: Partial<
    Pick<
      ToolSlice,
      | 'qualityPreset'
      | 'maxElementSize'
      | 'minElementSize'
      | 'elementOrder'
      | 'ansysMode'
      | 'meshStrategy'
      | 'curvatureAdaptive'
      | 'meshType'
      | 'simulationType'
      | 'elementType'
    >
  >,
  cadDiagonal: number | null,
  cadBounds: { lx: number; ly: number; lz: number } | null,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...patch };
  const preset = patch.qualityPreset;
  const hasExplicitMax = 'maxElementSize' in patch;
  const hasExplicitMin = 'minElementSize' in patch;

  // Match UI behavior: changing preset should also update min/max sizing
  // unless the caller explicitly provided custom size values.
  if (
    typeof preset === 'string' &&
    preset !== 'Custom' &&
    !hasExplicitMax &&
    !hasExplicitMin
  ) {
    const diagonal = cadDiagonal ?? 100;
    const geo: GeometryCharacteristics = cadBounds
      ? {
          diagonal,
          maxDimension: Math.max(cadBounds.lx, cadBounds.ly, cadBounds.lz),
          minDimension: Math.min(cadBounds.lx, cadBounds.ly, cadBounds.lz),
        }
      : { diagonal, maxDimension: diagonal, minDimension: diagonal };
    const sizes = computePresetSizes(geo)[preset];
    if (sizes) {
      normalized.maxElementSize = sizes.max;
      normalized.minElementSize = sizes.min;
    }
  }

  return normalized;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const toolInitialState: Omit<
  ToolSlice,
  | `set${string}`
  | 'resetMeshParamsState'
  | 'addWallGroup'
  | 'removeWallGroup'
  | 'resetExternalFaceOverrides'
  | 'clearOpeningEntries'
  | 'updateRefinementZone'
> = {
  qualityPreset: DEFAULTS.QUALITY_PRESET,
  maxElementSize: DEFAULTS.MAX_ELEMENT_SIZE,
  minElementSize: DEFAULTS.MIN_ELEMENT_SIZE,
  elementOrder: DEFAULTS.ELEMENT_ORDER,
  volumeCoupling: 'shared_nodes',
  ansysMode: DEFAULTS.ANSYS_MODE,
  meshStrategy: DEFAULTS.MESH_STRATEGY,
  curvatureAdaptive: DEFAULTS.CURVATURE_ADAPTIVE,
  meshType: DEFAULTS.MESH_TYPE,
  simulationType: DEFAULTS.SIMULATION_TYPE,
  elementType: DEFAULTS.ELEMENT_TYPE,
  strategyTimeoutS: null,

  // snap_explicit_feature_snap now defaults OFF (absent = off). It was FE
  // default-on ahead of the backend's PR-D flip, but that explicit feature-edge
  // snap pass is expensive and can over-constrain snap on noisy tessellation, so
  // it's back to opt-in — the user turns it on in the Snap Fidelity section when
  // they need sharper edge capture. Absent (not `false`) matches the strip-on-
  // null contract: setEngineOverrides + buildMeshParams drop it so the wire body
  // is byte-identical to the BE default. See components/mesh/EngineOverridesGrid.tsx.
  //
  // Per-run override defaults (single source: PER_RUN_OVERRIDE_DEFAULTS). Today
  // that's bypass_dirty_cad_gates: true — dirty-CAD gates bypassed by default
  // for every pipeline (FEA/CFD/CHT), so a body attempts meshing instead of
  // being blocked up front (reverses ENG-998's default-OFF). `true` is a real
  // value (not stripped), so it ships on the wire + the toggle shows checked;
  // users untick to re-arm. Rehydrate resets to the same const.
  engineOverrides: { ...PER_RUN_OVERRIDE_DEFAULTS },

  stlBeforeMeshEnabled: DEFAULTS.STL.BEFORE_MESH_ENABLED,
  stlSurfaceFactor: DEFAULTS.STL.SURFACE_FACTOR,
  stlCurvaturePts: DEFAULTS.STL.CURVATURE_PTS,
  stlMinCircleNodes: DEFAULTS.STL.MIN_CIRCLE_NODES,

  maxSizeStr: String(DEFAULTS.MAX_ELEMENT_SIZE),
  minSizeStr: String(DEFAULTS.MIN_ELEMENT_SIZE),
  stlSurfaceFactorStr: String(DEFAULTS.STL.SURFACE_FACTOR),
  stlCurvaturePtsStr: String(DEFAULTS.STL.CURVATURE_PTS),
  stlMinCircleNodesStr: String(DEFAULTS.STL.MIN_CIRCLE_NODES),

  // Initial state mirrors the FEA default (no enclosure, 'fea' mode).
  // Clicking CFD/CHT sets the appropriate domain + enclosure in the
  // MeshSettingsPanel handler.
  enclosureEnabled: false,
  faceTagAssignments: [],
  boundaryAssignmentOpen: false,
  bodyClassificationOpen: false,
  structuralBodiesOpen: false,
  blOpen: false,
  blAutoInitializedFor: null,
  advancedOverridesOpen: false,
  domainAbsoluteMode: false,
  externalFaceOverrides: {},
  domainParams: {
    mode: 'fea',
    upstream_factor: DOMAIN_DEFAULTS.UPSTREAM_FACTOR,
    downstream_factor: DOMAIN_DEFAULTS.DOWNSTREAM_FACTOR,
    lateral_y_factor: DOMAIN_DEFAULTS.LATERAL_Y_FACTOR,
    lateral_z_factor: DOMAIN_DEFAULTS.LATERAL_Z_FACTOR,
  },
  directionFactorStrs: Object.fromEntries(
    ALL_DIRECTION_KEYS.map((k) => [
      k,
      String(
        resolveDirectionFactors({
          upstream_factor: DOMAIN_DEFAULTS.UPSTREAM_FACTOR,
          downstream_factor: DOMAIN_DEFAULTS.DOWNSTREAM_FACTOR,
          lateral_y_factor: DOMAIN_DEFAULTS.LATERAL_Y_FACTOR,
          lateral_z_factor: DOMAIN_DEFAULTS.LATERAL_Z_FACTOR,
        })[k],
      ),
    ]),
  ) as Record<DirectionKey, string>,

  boundaryLayersEnabled: BL.ENABLED,
  blLayers: BL.LAYERS,
  blRatio: BL.RATIO,
  blThicknessUser: BL.THICKNESS_USER,
  blLayersStr: String(BL.LAYERS),
  blRatioStr: String(BL.RATIO),
  blThicknessUserStr: String(BL.THICKNESS_USER),
  blYPlus: null,
  blRe: null,
  blGlobalEnabled: false,
  blPatchNames: undefined,
  cavityStlRendered: false,
  internalFlowMeshSource: 'fluid_domain',
  wallGroups: [],
  bodyRegions: [],
  bodyRegionsMemo: null,
  qualityTarget: null,
  // AI retry is ON by default. It is the loop that turns a failed mesh into a
  // usable one without the user doing anything, and shipped off it reached
  // almost nobody — the opt-in sat in a collapsed panel's footer, so the whole
  // feature only ever appeared to people who had already found it. A
  // deliberate un-tick still sticks: `useMeshAutoRetryRestore` reads it back on
  // every load, and it persists as an explicit `{enabled: false}` so it is never
  // confused with the `null` the old default-off wrote (ENG-2066).
  meshAutoRetry: {
    enabled: true,
    max_attempts: DEFAULT_MESH_AUTO_RETRY_MAX_ATTEMPTS,
  },
  bodyClassificationView: 'cavity',
  refinementZones: [],
  refinementZonesOpen: false,
};

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createToolSlice: StateCreator<
  StoreState,
  [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]],
  [],
  ToolSlice
> = (set, get) => ({
  ...toolInitialState,

  setQualityPreset: (v) => set({ qualityPreset: v }),
  setMaxElementSize: (v) => set({ maxElementSize: v, maxSizeStr: String(v) }),
  setMinElementSize: (v) => set({ minElementSize: v, minSizeStr: String(v) }),
  setElementOrder: (v) => set({ elementOrder: v }),
  setAnsysMode: (v) => set({ ansysMode: v }),
  setMeshStrategy: (v) => set({ meshStrategy: v }),
  setStrategyTimeoutS: (v) => set({ strategyTimeoutS: v }),
  setCurvatureAdaptive: (v) => set({ curvatureAdaptive: v }),
  setMeshType: (v) => set({ meshType: v }),
  setSimulationType: (v) =>
    set((state) => ({
      simulationType: v,
      ...(v === 'fea' && {
        boundaryLayersEnabled: false,
      }),
      ...(v === 'cfd' && {
        domainParams: { ...state.domainParams, mode: 'cfd_external' as const },
        boundaryAssignmentOpen: false,
      }),
    })),
  setElementType: (v) => set({ elementType: v }),
  setStlBeforeMeshEnabled: (v) => set({ stlBeforeMeshEnabled: v }),
  setStlSurfaceFactor: (v) =>
    set({ stlSurfaceFactor: v, stlSurfaceFactorStr: String(v) }),
  setStlCurvaturePts: (v) =>
    set({ stlCurvaturePts: v, stlCurvaturePtsStr: String(v) }),
  setStlMinCircleNodes: (v) =>
    set({ stlMinCircleNodes: v, stlMinCircleNodesStr: String(v) }),
  setMaxSizeStr: (v) => set({ maxSizeStr: v }),
  setMinSizeStr: (v) => set({ minSizeStr: v }),
  setStlSurfaceFactorStr: (v) => set({ stlSurfaceFactorStr: v }),
  setStlCurvaturePtsStr: (v) => set({ stlCurvaturePtsStr: v }),
  setStlMinCircleNodesStr: (v) => set({ stlMinCircleNodesStr: v }),
  setEnclosureEnabled: (v) => set({ enclosureEnabled: v }),
  setFaceTagAssignments: (v) => set({ faceTagAssignments: v }),
  setBoundaryAssignmentOpen: (v) => set({ boundaryAssignmentOpen: v }),
  setBodyClassificationOpen: (v) => set({ bodyClassificationOpen: v }),
  setStructuralBodiesOpen: (v) => set({ structuralBodiesOpen: v }),
  setBlOpen: (v) => set({ blOpen: v }),
  setBlAutoInitializedFor: (v) => set({ blAutoInitializedFor: v }),
  setAdvancedOverridesOpen: (v) => set({ advancedOverridesOpen: v }),
  setDomainAbsoluteMode: (v) => set({ domainAbsoluteMode: v }),
  setExternalFaceOverride: (faceKey, type) =>
    set((s) => {
      if (type == null && !(faceKey in s.externalFaceOverrides)) return s;
      const next = { ...s.externalFaceOverrides };
      if (type == null) delete next[faceKey];
      else next[faceKey] = type;
      return { externalFaceOverrides: next };
    }),
  resetExternalFaceOverrides: () => set({ externalFaceOverrides: {} }),
  setDomainParams: (patch) =>
    set((state) => {
      const merged = { ...state.domainParams, ...patch };
      const resolved = resolveDirectionFactors(merged);
      return {
        domainParams: merged,
        directionFactorStrs: Object.fromEntries(
          ALL_DIRECTION_KEYS.map((k) => [k, String(resolved[k])]),
        ) as Record<DirectionKey, string>,
      };
    }),
  // A saved name or ignore binds to one part's detected openings, so it never
  // carries into another project.
  clearOpeningEntries: () => {
    const { opening_names, ignored_openings } = get().domainParams;
    // Nothing saved: skip the write entirely, so no subscriber re-renders and
    // devtools records no snapshot for a no-op.
    if (!(opening_names?.length || ignored_openings?.length)) return;
    set((s) => ({
      domainParams: {
        ...s.domainParams,
        opening_names: [],
        ignored_openings: [],
      },
    }));
  },
  setDirectionFactorStr: (key, v) =>
    set((state) => ({
      directionFactorStrs: { ...state.directionFactorStrs, [key]: v },
    })),
  setEngineOverrides: (v) =>
    set((state) => ({
      engineOverrides: Object.fromEntries(
        Object.entries({ ...state.engineOverrides, ...v }).filter(
          ([, val]) => val != null,
        ),
      ) as EngineOverrides,
    })),
  setBoundaryLayersEnabled: (v) => set({ boundaryLayersEnabled: v }),
  setBlLayers: (v) => set({ blLayers: v, blLayersStr: String(v) }),
  setBlRatio: (v) => set({ blRatio: v, blRatioStr: String(v) }),
  setBlThicknessUser: (v) =>
    set({ blThicknessUser: v, blThicknessUserStr: String(v) }),
  setBlLayersStr: (v) => set({ blLayersStr: v }),
  setBlRatioStr: (v) => set({ blRatioStr: v }),
  setBlThicknessUserStr: (v) => set({ blThicknessUserStr: v }),
  setBlYPlus: (v) => set({ blYPlus: v }),
  setBlRe: (v) => set({ blRe: v }),
  setBlGlobalEnabled: (v) => set({ blGlobalEnabled: v }),
  setBlPatchNames: (v) => set({ blPatchNames: v }),
  setCavityStlRendered: (v) => set({ cavityStlRendered: v }),
  // Flipping the source invalidates every face pick made under the old one:
  // `faceTagAssignments` and `wallGroups` both key on a bare `face_index`, and
  // the cavity and body are different index spaces (4 cavity faces vs 8 body
  // faces on a bored block), so a carried-over label names an unrelated face.
  //
  // The clear lives on the store write rather than in a panel effect because
  // `MeshSourceToggle` renders in three panel headers (BA / BL / Local
  // Refinement) that `MeshAuthoringSection` mounts mutually exclusively — a
  // flip from Local Refinement would otherwise leave BA's stale labels intact
  // for the next submit, which is the ENG-1392 failure with extra steps.
  setInternalFlowMeshSource: (v) =>
    set((s) =>
      s.internalFlowMeshSource === v
        ? { internalFlowMeshSource: v }
        : {
            internalFlowMeshSource: v,
            faceTagAssignments: [],
            wallGroups: [],
            blPatchNames: undefined,
          },
    ),
  setWallGroups: (groups) => set({ wallGroups: groups }),
  setBodyRegions: (regions) => set({ bodyRegions: regions }),
  setBodyRegionsMemo: (memo) => set({ bodyRegionsMemo: memo }),
  setBodyClassificationView: (v) => set({ bodyClassificationView: v }),
  setQualityTarget: (v) => set({ qualityTarget: v }),
  setMeshAutoRetry: (cfg) =>
    set((s) =>
      // No-op identity guard — skip the set when the value is unchanged so
      // subscribers don't churn.
      sameAutoRetryConfig(s.meshAutoRetry, cfg) ? s : { meshAutoRetry: cfg },
    ),
  setRefinementZones: (zones) => set({ refinementZones: zones }),
  updateRefinementZone: (id, patch) =>
    set((state) => ({
      refinementZones: state.refinementZones.map((z) =>
        z.id === id ? ({ ...z, ...patch } as RefinementZoneDraft) : z,
      ),
    })),
  setRefinementZonesOpen: (v) => set({ refinementZonesOpen: v }),
  addWallGroup: (group) =>
    set((state) =>
      state.wallGroups.some((g) => g.face_index === group.face_index)
        ? state
        : { wallGroups: [...state.wallGroups, group] },
    ),
  removeWallGroup: (face_index) =>
    set((state) => ({
      wallGroups: state.wallGroups.filter((g) => g.face_index !== face_index),
    })),
  setWallGroupLabel: (face_index, label) =>
    set((state) => ({
      wallGroups: state.wallGroups.map((g) =>
        g.face_index === face_index ? { ...g, label } : g,
      ),
    })),

  // Aggregate setters — auto-sync string variants
  setMeshSettings: (patch) =>
    set((state) => ({
      ...syncStrings(
        applyPresetSizesForMeshPatch(
          withLinearOrderOffFeaTet(patch),
          state.cadDiagonal,
          state.cadBounds,
        ),
      ),
      ...modeFollowingPhysics(patch.simulationType, state.domainParams),
    })),
  setBoundaryLayerSettings: (patch) => set(syncStrings(patch)),
  setStlSettings: (patch) => set(syncStrings(patch)),

  resetMeshParamsState: () => set({ ...toolInitialState }),
});
